import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Plus, Search, CheckCircle, Clock, AlertTriangle, QrCode, FileDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import api from '@/services/api';
import { toast } from 'sonner';
import DocumentQRCode from '@/components/DocumentQRCode';

const StatusBadge = ({ statut, validiteType }) => {
  if (validiteType === 'vie') {
    return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />À vie</Badge>;
  }
  if (statut === 'valide') return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Valide</Badge>;
  if (statut === 'bientot_echu') return <Badge className="bg-orange-100 text-orange-800"><Clock className="h-3 w-3 mr-1" />Bientôt échu</Badge>;
  if (statut === 'echu') return <Badge variant="destructive"><AlertTriangle className="h-3 w-3 mr-1" />Échu</Badge>;
  return <Badge variant="outline">{statut}</Badge>;
};

export default function Formations() {
  const [formations, setFormations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statutFilter, setStatutFilter] = useState('all');

  useEffect(() => {
    loadFormations();
  }, []);

  const loadFormations = async () => {
    try {
      setLoading(true);
      const data = await api.getAllFormations();
      setFormations(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des formations');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAttestation = async (formation) => {
    try {
      toast.info('Génération de l\'attestation en cours...');
      const result = await api.generateFormationAttestation(formation.id);
      
      if (result.html) {
        // Extract styles from the original HTML
        const styleMatch = result.html.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
        const originalStyles = styleMatch ? styleMatch[0] : '';
        
        // Extract body content from the original HTML
        const bodyMatch = result.html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
        const bodyContent = bodyMatch ? bodyMatch[1] : result.html.replace(/<html>|<\/html>|<head>.*?<\/head>|<!DOCTYPE html>/gis, '');
        
        // Create a Blob with the HTML content including original styles
        const htmlWithPrintStyles = `
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="UTF-8">
            <title>Attestation de Formation - ${formation.collaborateur_nom || ''}</title>
            ${originalStyles}
            <style>
              @media print {
                body { margin: 0; }
                .no-print { display: none !important; }
              }
            </style>
          </head>
          <body>
            <div class="no-print" style="position: fixed; top: 10px; right: 10px; z-index: 9999;">
              <button onclick="window.print()" style="padding: 12px 24px; background: #059669; color: white; border: none; border-radius: 8px; cursor: pointer; font-size: 14px; font-weight: bold;">
                🖨️ Imprimer / Enregistrer en PDF
              </button>
              <p style="font-size: 12px; color: #666; margin-top: 8px;">Astuce: Choisissez "Enregistrer en PDF" comme imprimante</p>
            </div>
            ${bodyContent}
          </body>
          </html>
        `;
        
        // Create blob and open in new tab
        const blob = new Blob([htmlWithPrintStyles], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        
        toast.success('Attestation ouverte - Utilisez le bouton pour enregistrer en PDF');
      } else {
        throw new Error('Aucun contenu généré');
      }
    } catch (error) {
      console.error('Error generating attestation:', error);
      toast.error('Erreur lors de la génération de l\'attestation: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const filteredFormations = formations.filter(formation => {
    const matchesSearch = !searchTerm ||
      formation.intitule?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      formation.collaborateur_nom?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (statutFilter === 'all') return matchesSearch;
    if (statutFilter === 'vie') return matchesSearch && formation.validite_type === 'vie';
    if (statutFilter === 'valide') return matchesSearch && formation.statut === 'valide';
    if (statutFilter === 'bientot_echu') return matchesSearch && formation.statut === 'bientot_echu';
    if (statutFilter === 'echu') return matchesSearch && formation.statut === 'echu';
    return matchesSearch;
  });

  // Count unique formations
  const uniqueFormations = [...new Set(formations.map(f => f.intitule))];

  const stats = {
    total: formations.length,
    valides: formations.filter(f => f.statut === 'valide' || f.validite_type === 'vie').length,
    expirant: formations.filter(f => f.statut === 'bientot_echu').length,
    echus: formations.filter(f => f.statut === 'echu').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <GraduationCap className="w-7 h-7 text-blue-600" />
            Formations internes
          </h1>
          <p className="text-gray-500 mt-1">
            {formations.length} formation{formations.length > 1 ? 's' : ''} • {uniqueFormations.length} type{uniqueFormations.length > 1 ? 's' : ''} différent{uniqueFormations.length > 1 ? 's' : ''}
          </p>
        </div>
        <Link to="/gestion-formations">
          <Button className="gap-2">
            <Plus className="w-4 h-4" />
            Gérer les formations
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-100">
                <GraduationCap className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-green-100">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Valides</p>
                <p className="text-2xl font-bold">{stats.valides}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-orange-100">
                <Clock className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Expirant bientôt</p>
                <p className="text-2xl font-bold">{stats.expirant}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-red-100">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Échus</p>
                <p className="text-2xl font-bold">{stats.echus}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher par intitulé, collaborateur..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statutFilter} onValueChange={setStatutFilter}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Tous les statuts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="valide">Valides</SelectItem>
                <SelectItem value="vie">À vie</SelectItem>
                <SelectItem value="bientot_echu">Expirant bientôt</SelectItem>
                <SelectItem value="echu">Échus</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredFormations.length === 0 ? (
            <div className="text-center py-12">
              <GraduationCap className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">Aucune formation trouvée</p>
              <Link to="/gestion-formations">
                <Button variant="link">Ajouter une formation</Button>
              </Link>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Collaborateur</TableHead>
                  <TableHead>Formation</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Validité</TableHead>
                  <TableHead>Expiration</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredFormations.map((formation) => (
                  <TableRow key={formation.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{formation.collaborateur_nom || 'N/A'}</p>
                        <p className="text-sm text-gray-500">{formation.collaborateur_matricule}</p>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{formation.intitule}</TableCell>
                    <TableCell>
                      {formation.date_formation ? new Date(formation.date_formation).toLocaleDateString('fr-FR') : '-'}
                    </TableCell>
                    <TableCell>
                      {formation.validite_type === 'vie' ? 'À vie' : `${formation.validite_mois || '-'} mois`}
                    </TableCell>
                    <TableCell>
                      {formation.date_fin ? new Date(formation.date_fin).toLocaleDateString('fr-FR') : '-'}
                    </TableCell>
                    <TableCell>
                      <StatusBadge statut={formation.statut} validiteType={formation.validite_type} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {(formation.statut === 'valide' || formation.validite_type === 'vie') && (
                          <>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              title="Télécharger l'attestation"
                              onClick={() => handleDownloadAttestation(formation)}
                            >
                              <FileDown className="h-4 w-4 text-green-600" />
                            </Button>
                            <DocumentQRCode 
                              docType="formation" 
                              docId={formation.id}
                              documentTitle={formation.intitule}
                            />
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
